/**
 * Pretium+ Offline Sync Engine
 * Replays queued offline records back to Supabase when connectivity restores.
 */

import { createClient } from '@/lib/supabase/client';
import {
  getPendingRecords,
  getFailedRecords,
  markRecordSyncing,
  markRecordSynced,
  markRecordFailed,
  clearSyncedRecords,
  OfflineRecord,
} from '@/lib/offlineDB';

export interface SyncResult {
  total: number;
  synced: number;
  failed: number;
  errors: { id: string; type: string; error: string }[];
}

async function syncRecord(record: OfflineRecord): Promise<void> {
  const supabase = createClient();
  await markRecordSyncing(record.id);

  try {
    switch (record.type) {
      case 'fnol': {
        const { error } = await supabase.from('fnol_records').insert(record.payload);
        if (error) throw new Error(error.message);
        break;
      }
      case 'motor_claim': {
        const { error } = await supabase.from('motor_claims').insert(record.payload);
        if (error) throw new Error(error.message);
        break;
      }
      case 'breakdown_job': {
        const { error } = await supabase.from('breakdown_jobs').insert(record.payload);
        if (error) throw new Error(error.message);
        break;
      }
      case 'cs_call': {
        // CS calls are stored as fnol_records with claim_type = 'customer_service'
        const { error } = await supabase.from('fnol_records').insert(record.payload);
        if (error) throw new Error(error.message);
        break;
      }
      default:
        throw new Error(`Unknown record type: ${(record as OfflineRecord).type}`);
    }
    await markRecordSynced(record.id);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    await markRecordFailed(record.id, message);
    throw err;
  }
}

export async function runSync(): Promise<SyncResult> {
  const pending = await getPendingRecords();
  const failed = await getFailedRecords();
  // Retry failed records that have fewer than 3 attempts
  const toSync = [
    ...pending,
    ...failed.filter((r) => r.syncAttempts < 3),
  ];

  const result: SyncResult = { total: toSync.length, synced: 0, failed: 0, errors: [] };

  for (const record of toSync) {
    try {
      await syncRecord(record);
      result.synced++;
    } catch (err) {
      result.failed++;
      result.errors.push({
        id: record.id,
        type: record.type,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  // Clean up successfully synced records
  await clearSyncedRecords();

  return result;
}
